// HVNMDE — Scroll choreography (homepage showroom layer)
// GSAP ScrollTrigger scrub engine: hero media drift + canvas frame-sequence build sections.
// Static-first contract: every section this file touches is fully usable without it.
// Conversion code (cart, PDP, drawers) lives in motion.js and NEVER depends on this file.

(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function boot() {
    // Graceful exit: CDN blocked, old browser, reduced motion → static layout stands.
    if (reduced || !window.gsap || !window.ScrollTrigger) {
      document.documentElement.classList.add('no-scrub');
      return;
    }
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);

    initHeroDrift(gsap);
    initWordScrub(gsap);
    initMediaScrub(gsap);
    initEgeonReveals(gsap, ST);
    initBuildSections(gsap, ST);

    // Layout settles late (webfonts, posters) — recalc pin geometry.
    window.addEventListener('load', () => ST.refresh(), { once: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ST.refresh());
  }

  // — Hero: slow media scale + drift while the hero scrolls out (Janis-style calm)
  function initHeroDrift(gsap) {
    const media = document.querySelector('[data-hero-media]');
    if (!media) return;
    gsap.fromTo(
      media,
      { scale: 1, yPercent: 0 },
      {
        scale: 1.06,
        yPercent: 5,
        ease: 'none',
        scrollTrigger: {
          trigger: media.closest('.hero') || media,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  }

  // — Egeon smooth reveals (clip wipe + rise, staggered). ScrollTrigger.batch is
  //   reliable on bottom-of-page sections where the IO bottom-margin never fires.
  function initEgeonReveals(gsap, ST) {
    const els = document.querySelectorAll('.reveal-egeon');
    if (!els.length) return;
    ST.batch(els, {
      start: 'top 88%',
      once: true,
      onEnter: (batch) => batch.forEach((el, i) => setTimeout(() => el.classList.add('is-in'), i * 110)),
    });
    // Safety: anything already above the fold on load reveals immediately.
    requestAnimationFrame(() => els.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) el.classList.add('is-in');
    }));
  }

  // — Word scrub (Egeon signature, measured: scrub 1, reveal window ~0.4 viewports)
  function initWordScrub(gsap) {
    document.querySelectorAll('[data-words-scrub]').forEach((el) => {
      const words = el.textContent.split(/\s+/).filter(Boolean);
      if (words.length < 3) { el.classList.add('no-split'); return; }
      el.innerHTML = words.map((w) => '<span class="w">' + w + '</span>').join(' ');
      gsap.to(el.querySelectorAll('.w'), {
        opacity: 1,
        ease: 'none',
        stagger: 0.1,
        scrollTrigger: { trigger: el, start: 'top 80%', end: 'top 35%', scrub: 1 },
      });
    });
  }

  // — Media scroll-mapping (Egeon primitives, measured):
  //   [data-zoom-scrub]  → slow scale 1 → 1.05 across the section
  //   [data-parallax]    → yPercent +6 → -6 (or the attribute value)
  function initMediaScrub(gsap) {
    document.querySelectorAll('[data-zoom-scrub]').forEach((el) => {
      gsap.fromTo(el, { scale: 1 }, {
        scale: 1.05,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
    document.querySelectorAll('[data-parallax]').forEach((el) => {
      const d = parseFloat(el.dataset.parallax) || 6;
      gsap.fromTo(el, { yPercent: d }, {
        yPercent: -d,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  }

  // — Build sections: pinned canvas frame scrub (Egeon pattern)
  function initBuildSections(gsap, ST) {
    document.querySelectorAll('[data-build]').forEach((section) => {
      if (section.dataset.buildMode === 'particles') return; // WebGL tier lands later — static until then
      const canvas = section.querySelector('[data-sequence]');
      if (!canvas) return;

      // Hard degrade tier: weak device / data saver → keep static poster layout.
      const nav = navigator;
      if ((nav.deviceMemory && nav.deviceMemory <= 2) || (nav.connection && nav.connection.saveData)) {
        section.classList.add('is-static');
        return;
      }

      const urls = readManifest(section);
      if (!urls || urls.length < 2) return;

      const player = createSequencePlayer(section, canvas, urls);
      if (!player) return;

      section.classList.add('is-scrub-ready');

      const counter = section.querySelector('[data-build-counter]');
      if (counter) counter.removeAttribute('hidden');
      const progress = section.querySelector('[data-build-progress]');
      // data-at ships as raw percent (0–100) — Liquid float division is unreliable across engines
      const callouts = [...section.querySelectorAll('[data-callout]')].map((el) => ({
        el,
        at: parseFloat(el.dataset.at || '50') / 100,
      }));
      const pad = (n) => String(n).padStart(3, '0');
      const last = urls.length - 1;

      // Begin fetching one viewport early so frames are warm when the pin starts.
      ST.create({
        trigger: section,
        start: 'top 150%',
        once: true,
        onEnter: () => player.load(),
      });

      const lengthVh = parseInt(section.dataset.scrubLength || '250', 10);
      ST.create({
        trigger: section,
        start: 'top top',
        end: '+=' + lengthVh + '%',
        pin: true,
        scrub: true, // Lenis already smooths input — numeric scrub would double-smooth
        anticipatePin: 1,
        onUpdate: (self) => {
          const p = self.progress;
          player.seek(p);
          if (counter) counter.textContent = 'FR ' + pad(Math.round(p * last)) + ' / ' + pad(last);
          if (progress) progress.style.transform = 'scaleX(' + p + ')';
          callouts.forEach((c) => c.el.classList.toggle('is-active', Math.abs(p - c.at) < 0.12));
        },
      });

      // Warm the head of the sequence when the browser is idle anyway.
      if ('requestIdleCallback' in window) {
        window.addEventListener(
          'load',
          () => requestIdleCallback(() => player.preloadHead(), { timeout: 4000 }),
          { once: true }
        );
      }
    });
  }

  function readManifest(section) {
    const wantMobile = window.matchMedia('(max-width: 720px)').matches;
    const pick =
      section.querySelector('[data-sequence-manifest-urls="' + (wantMobile ? 'mobile' : 'desktop') + '"]') ||
      section.querySelector('[data-sequence-manifest-urls="desktop"]');
    if (!pick) return null;
    try {
      return JSON.parse(pick.textContent);
    } catch (e) {
      return null;
    }
  }

  function createSequencePlayer(section, canvas, urls) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return null;

    const COUNT = urls.length;
    const KEY_STEP = 8;
    // Store HTMLImageElements (compressed bytes) — NOT ImageBitmaps:
    // ~100 decoded 720p bitmaps ≈ hundreds of MB; the browser's decode cache
    // handles hot frames, we just hint direction with img.decode().
    const frames = new Array(COUNT).fill(null);
    let loadStarted = false;
    let current = -1;
    let target = 0;

    function loadOne(i, cb) {
      if (frames[i]) { cb && cb(); return; }
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        frames[i] = img;
        if (i === target || (current === -1 && i === 0)) draw(nearestLoaded(target));
        cb && cb();
      };
      img.onerror = () => { cb && cb(); };
      img.src = urls[i];
    }

    function load() {
      if (loadStarted) return;
      loadStarted = true;
      // keyframes first (every 8th) — guarantees a max visual gap of 8 frames immediately…
      for (let i = 0; i < COUNT; i += KEY_STEP) loadOne(i);
      loadOne(COUNT - 1);
      // …then fill the rest ordered by distance from the current scrub position
      const rest = [];
      for (let i = 0; i < COUNT; i++) if (i % KEY_STEP !== 0 && i !== COUNT - 1) rest.push(i);
      rest.sort((a, b) => Math.abs(a - target) - Math.abs(b - target));
      let cursor = 0;
      (function next() {
        let inFlight = 0;
        while (cursor < rest.length && inFlight < 6) {
          inFlight++;
          loadOne(rest[cursor++], () => { inFlight--; next(); });
        }
      })();
    }

    function preloadHead() { for (let i = 0; i < Math.min(COUNT, 9); i++) loadOne(i); }

    function nearestLoaded(i) {
      if (frames[i]) return i;
      for (let d = 1; d < COUNT; d++) {
        if (frames[i - d]) return i - d;
        if (frames[i + d]) return i + d;
      }
      return -1;
    }

    // — canvas sizing: backing store = css box × DPR (capped at 2)
    function resize() {
      const r = section.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(Math.max(1, r.width) * dpr);
      const h = Math.round(Math.max(1, window.innerHeight) * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        if (current >= 0) { const c = current; current = -1; draw(c); }
      }
    }
    resize();
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(section);
    else window.addEventListener('resize', resize, { passive: true });

    function draw(i) {
      if (i < 0 || i === current) return;
      const img = frames[i];
      if (!img) return;
      current = i;
      // object-fit: cover crop math
      const cw = canvas.width, ch = canvas.height;
      const iw = img.naturalWidth, ih = img.naturalHeight;
      const s = Math.max(cw / iw, ch / ih);
      const sw = cw / s, sh = ch / s;
      const sx = (iw - sw) / 2, sy = (ih - sh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    }

    function seek(p) {
      target = Math.max(0, Math.min(COUNT - 1, Math.round(p * (COUNT - 1))));
      draw(nearestLoaded(target));
      // decode-ahead hint in scrub direction
      for (let d = 1; d <= 5; d++) {
        const n = frames[target + d];
        if (n && n.decode) n.decode().catch(() => {});
      }
    }

    return { load, seek, preloadHead };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
